import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/global.css';

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

interface UploadResponse {
  status: string;
  task_id?: string;
  download_url?: string;
  ai_response?: string;
  duration?: number;
  logs?: string[];
  error?: string;
  message?: string;
}

const PyA3: React.FC = () => {
  const { user } = useAuth();
  const [inputType, setInputType] = useState<'file' | 'url'>('file');
  const [processingMode, setProcessingMode] = useState<'transcribe_only' | 'transcribe_and_ai'>('transcribe_only');
  const [aiPrompt, setAiPrompt] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [aiResponse, setAiResponse] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [taskId, setTaskId] = useState<string>('');

  useEffect(() => {
    // Очистка старых логов при размонтировании
    return () => {
      setLogs([]);
    };
  }, []);

  const addLog = (logsData: string[] | string, isError = false) => {
    const logsArray = Array.isArray(logsData) ? logsData : [logsData];
    const newLogs = logsArray.map(log => {
      // Убираем временную метку и уровень лога от бэкенда
      const cleanMessage = log.replace(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3} - (INFO|WARNING|ERROR|CRITICAL) - /, '');
      return {
        timestamp: new Date().toLocaleTimeString(),
        level: isError ? 'ERROR' : 'INFO',
        message: cleanMessage
      };
    });
    setLogs(prev => [...prev, ...newLogs]);
  };

  const pollStatus = async (taskId: string, delay = 10000) => {
    setTimeout(async () => {
      try {
        const response = await fetch(`/api/pya3/status/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const data: UploadResponse = await response.json();
        
        if (data.logs) addLog(data.logs, !response.ok);
        
        if (data.status === 'success') {
          handleSuccess(data);
        } else if (data.status === 'error') {
          setError(data.message || 'Неизвестная ошибка API.');
          setLoading(false);
        } else {
          // queued / processing → продолжаем опрос
          let nextDelay = delay;
          if (data.duration) {
            const duration = data.duration;
            nextDelay = Math.max(3000, Math.round((duration / 15) * 1000));
            addLog(`Следующая проверка статуса через ${Math.round(nextDelay / 1000)} секунд`);
          }
          pollStatus(taskId, nextDelay);
        }
      } catch (err: any) {
        addLog(`Ошибка опроса статуса: ${err.message}`, true);
        setLoading(false);
        setError('Ошибка проверки статуса задачи');
      }
    }, delay);
  };

  const handleSuccess = (data: UploadResponse) => {
    setLoading(false);
    setSuccess('Обработка успешно завершена!');
    
    if (data.ai_response) {
      setAiResponse(data.ai_response);
      addLog('Обработка ИИ завершена!');
    } else {
      addLog('Транскрипция успешно завершена!');
    }
    
    if (data.download_url) {
      setDownloadUrl(data.download_url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setLogs([]);
    setAiResponse('');
    setDownloadUrl('');
    setTaskId('');

    addLog('Начало нового запроса...');

    // Валидация
    if (inputType === 'file' && !selectedFile) {
      setError('Пожалуйста, выберите файл для загрузки.');
      setLoading(false);
      return;
    }

    if (inputType === 'url' && !youtubeUrl.trim()) {
      setError('Пожалуйста, вставьте ссылку на YouTube.');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('input_type', inputType);
      formData.append('processing_mode', processingMode);
      formData.append('ai_prompt', processingMode === 'transcribe_and_ai' ? aiPrompt : '');
      
      if (inputType === 'file' && selectedFile) {
        formData.append('file', selectedFile);
      }
      
      if (inputType === 'url') {
        formData.append('youtube_url', youtubeUrl);
      }

      const response = await fetch('/api/pya3/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data: UploadResponse = await response.json();
      
      if (data.logs) addLog(data.logs, !response.ok);
      
      if (!response.ok) {
        throw new Error(data.error || 'Ошибка на сервере.');
      }

      if (data.status === 'success') {
        handleSuccess(data);
      } else if (data.status === 'queued' && data.task_id) {
        setTaskId(data.task_id);
        pollStatus(data.task_id);
      } else {
        throw new Error('Некорректный ответ от сервера.');
      }
    } catch (err: any) {
      addLog(`Критическая ошибка: ${err.message}`, true);
      setLoading(false);
      setError(err.message || 'Произошла ошибка при обработке запроса');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const downloadResult = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <div className="container py-4">
      <h1 className="mb-4" style={{ color: 'var(--primary-orange)' }}>
        Сервис транскрипции и анализа аудио
      </h1>

      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <h3>1. Выберите способ загрузки</h3>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="input_type"
                  id="type_file"
                  value="file"
                  checked={inputType === 'file'}
                  onChange={(e) => setInputType(e.target.value as 'file' | 'url')}
                  required
                />
                <label className="form-check-label" htmlFor="type_file">Файл</label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="input_type"
                  id="type_url"
                  value="url"
                  checked={inputType === 'url'}
                  onChange={(e) => setInputType(e.target.value as 'file' | 'url')}
                  required
                />
                <label className="form-check-label" htmlFor="type_url">Ссылка YouTube</label>
              </div>
              
              {inputType === 'file' && (
                <div className="mt-3">
                  <label htmlFor="file" className="form-label">Прикрепите аудио/видео файл:</label>
                  <input
                    type="file"
                    id="file"
                    className="form-control"
                    accept="audio/*,video/*"
                    onChange={handleFileChange}
                  />
                </div>
              )}
              
              {inputType === 'url' && (
                <div className="mt-3">
                  <label htmlFor="youtube_url" className="form-label">Вставьте ссылку:</label>
                  <input
                    type="text"
                    id="youtube_url"
                    className="form-control"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="mb-4">
              <h3>2. Выберите режим обработки</h3>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processing_mode"
                  id="mode_transcribe"
                  value="transcribe_only"
                  checked={processingMode === 'transcribe_only'}
                  onChange={(e) => setProcessingMode(e.target.value as 'transcribe_only' | 'transcribe_and_ai')}
                  required
                />
                <label className="form-check-label" htmlFor="mode_transcribe">Только расшифровка</label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processing_mode"
                  id="mode_ai"
                  value="transcribe_and_ai"
                  checked={processingMode === 'transcribe_and_ai'}
                  onChange={(e) => setProcessingMode(e.target.value as 'transcribe_only' | 'transcribe_and_ai')}
                  required
                />
                <label className="form-check-label" htmlFor="mode_ai">Расшифровка + Анализ ИИ</label>
              </div>
              
              {processingMode === 'transcribe_and_ai' && (
                <div className="mt-3">
                  <label htmlFor="ai_prompt" className="form-label">Ваш запрос для ИИ:</label>
                  <textarea
                    id="ai_prompt"
                    className="form-control"
                    placeholder="Например: Сделай краткую выжимку из этого текста."
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? 'Обработка...' : 'Отправить на обработку'}
            </button>
          </form>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <strong>Ошибка:</strong> {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <strong>Успех!</strong> {success}
        </div>
      )}

      {logs.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h3>Лог выполнения</h3>
            <div style={{
              backgroundColor: '#333',
              color: '#0f0',
              fontFamily: "'Courier New', Courier, monospace",
              padding: '10px',
              borderRadius: '4px',
              height: '200px',
              overflowY: 'auto',
              fontSize: '0.9em'
            }}>
              {logs.map((log, index) => (
                <div key={index} style={{ color: log.level === 'ERROR' ? '#f00' : '#0f0' }}>
                  [{log.timestamp}] {log.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {aiResponse && (
        <div className="card mb-4">
          <div className="card-body">
            <h3>Ответ от ИИ</h3>
            <pre style={{
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              {aiResponse}
            </pre>
          </div>
        </div>
      )}

      {downloadUrl && (
        <div className="card">
          <div className="card-body text-center">
            <button
              className="btn btn-success"
              onClick={downloadResult}
            >
              Скачать итоговый файл
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PyA3;
